import React, { forwardRef } from 'react';
import styled from 'styled-components';

interface CardContainerProps {
  color: string;
  isDragging: boolean;
  x: number;
  y: number;
  zIndex: number;
  width: number;
  height: number;
  style?: React.CSSProperties;
  children: React.ReactNode;
  attributes: any;
  listeners: any;
  onClick?: () => void;
}

// カードのスタイル - attrsメソッドを使用して頻繁に変更されるスタイルを最適化
const Container = styled.div.attrs<{
  $color: string;
  $isDragging: boolean;
  $zIndex: number;
  $x: number;
  $y: number;
  $width: number;
  $height: number;
}>(props => ({
  style: {
    left: `${props.$x}px`,
    top: `${props.$y}px`,
    width: `${props.$width}px`,
    height: `${props.$height}px`,
    zIndex: props.$isDragging ? 1000 : props.$zIndex,
    transform: props.style?.transform || 'none',
  },
}))<{
  $color: string;
  $isDragging: boolean;
  $zIndex: number;
  $x: number;
  $y: number;
  $width: number;
  $height: number;
}>`
  position: absolute;
  padding: 0;
  background-color: ${props => props.$color};
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  min-width: 200px;
  min-height: 150px;
  resize: none;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
`;

// ドラッグハンドル用のヘッダーバー
const DragHandle = styled.div`
  height: 30px;
  background-color: rgba(0, 0, 0, 0.05);
  cursor: move;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 5px 5px 0 0;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
  }
  
  &:active {
    background-color: rgba(0, 0, 0, 0.12);
  }
`;

// カードのコンテンツ部分
const CardContent = styled.div`
  padding: 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardContainer = forwardRef<HTMLDivElement, CardContainerProps>(
  ({ color, isDragging, x, y, zIndex, width, height, style, children, attributes, listeners, onClick }, ref) => {
    // 子要素を分割
    const childrenArray = React.Children.toArray(children);
    const headerElement = childrenArray[0]; // CardHeader
    const contentElements = childrenArray.slice(1); // その他のコンテンツ
    
    return (
      <Container
        ref={ref}
        style={style}
        $color={color}
        $isDragging={isDragging}
        $x={x}
        $y={y}
        $zIndex={zIndex}
        $width={width}
        $height={height}
        onClick={onClick}
      >
        <DragHandle {...attributes} {...listeners}>
          {headerElement}
        </DragHandle>
        <CardContent>
          {contentElements}
        </CardContent>
      </Container>
    );
  }
);

CardContainer.displayName = 'CardContainer';

export default CardContainer;
export type { CardContainerProps }; 